<template>
  <div :class="$style['record-page']" itemscope itemtype="http://schema.org/NewsArticle">
    <meta itemprop="identifier" :content="recordData.id" />
    <div v-if="isShowMatureOverlay" :class="$style['record-page-wrapper']" />
    <mature-content-modal
      :is-open="isMatureContentModalOpen"
      @closeModal="onMatureContentModalClose"
    />
    <app-layout>
      <template slot="left-column">
        <app-left-column />
      </template>

      <template v-if="hasRecordData" slot="central-column">
        <template v-for="(blockItem, index) of centralColumnBlocks">
          <AdvFlyroll
            v-if="blockItem.type === 'flyroll_advert'"
            :key="blockItem.type + index"
            :advData="blockItem"
          />
          <!-- eslint-disable-next-line vue/require-component-is -->
          <component
            v-else
            :key="blockItem.type + index"
            v-bind="bindComponent(blockItem)"
            :class="$style[blockItem.type]"
            @viewAdv="processingAdvView"
            @clickAdv="processingAdvClick"
          />
        </template>
        
        <AdvInfinityFeed v-if="isDesktop" />
      </template>

      <template slot="right-column">
        <app-right-column />
      </template>
    </app-layout>
    <record-image-viewer
      v-if="isImageViewerRendered"
      v-show="isImageViewerVisible"
      :class="$style['record-image-viewer']"
    />
    <FeedbackToErrorModal
      v-if="isFeedbackToErrorModalRendered"
      v-model="isFeedbackToErrorModalOpen"
    />
  </div>
</template>

<script>
import {
  SPARROW_BLOCK_USE_CASE_KEY,
  SparrowBlockUseCase,
  SMI2_BLOCK_USE_CASE_KEY,
  Smi2BlockUseCase,
  SMI24_BLOCK_USE_CASE_KEY,
  Smi24BlockUseCase
} from '@jtnews/shared/seedwork/frontend/application';
import {
  SparrowBlockAnalyticsService,
  AnalyticsBlockPlace,
  Smi2BlockAnalyticsService,
  Smi24BlockAnalyticsService
} from '@jtnews/shared/seedwork/frontend/domain';
import { BlockPlaceRepository } from '@jtnews/shared/seedwork/frontend/infrastructure';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { registerModule, unregisterModule, useModule } from 'vuex-simple';
import { RecordModule, RECORD_MODULE_NAMESPACE } from '../store';

import { components, blockNameToComponentMap } from '../configs/record';
import { blockToComponent } from '../../../shared/utils/block-to-component.utils';

import { PageAnalyticsMixin } from '../../../shared/mixins/page-analytics';
import { WindowScrollMixin } from '../../../shared/mixins/window-scroll.mixin';

import AppLayout from '../../../layout/containers/app-layout';
import AppLeftColumn from '../../../layout/containers/app-left-column';
import AppRightColumn from '../../../layout/containers/app-right-column';

import { AdvInfinityFeed } from '../../../shared/components/_adv/adv-infinity-feed';
import { AdvFlyroll } from '../../../shared/components/_adv/adv-flyroll';

const PAGE_NAME = 'Материал';

const FeedbackToErrorModal = () =>
  import(/* webpackChunkName: "feedback-to-error-modal" */ '@jtnews/layout/modals').then(
    m => m.FeedbackToErrorModal
  );

const RecordImageViewer = () =>
  import(
    /* webpackChunkName: "record-image-viewer" */ '../components/record-image-viewer'
  );

// eslint-disable-next-line @typescript-eslint/naming-convention
const MatureContentModal = () =>
  import(
    /* webpackChunkName: "mature-content-modal" */ '../containers/mature-content-modal'
  ).then(m => m.MatureContentModal);

export default {
  serverPrefetch() {
    return new Promise(resolve => {
      this.registerModule();
      resolve();
    });
  },
  name: 'RecordPage',
  components: {
    AppLayout,
    AppLeftColumn,
    AppRightColumn,
    RecordImageViewer,
    FeedbackToErrorModal,
    MatureContentModal,
    AdvInfinityFeed,
    AdvFlyroll,
    ...components
  },
  mixins: [WindowScrollMixin, PageAnalyticsMixin],
  provide() {
    return {
      [SPARROW_BLOCK_USE_CASE_KEY]: new SparrowBlockUseCase({
        deps: {
          store: this.store,
          analyticsService: new SparrowBlockAnalyticsService({
            pageName: PAGE_NAME,
            deps: {
              analytics: this.analyticsService,
              blockPlaceRepository: this.blockPlaceRepository
            }
          })
        }
      }),
      [SMI2_BLOCK_USE_CASE_KEY]: new Smi2BlockUseCase({
        deps: {
          store: this.store,
          analyticsService: new Smi2BlockAnalyticsService({
            pageName: PAGE_NAME,
            deps: {
              analytics: this.analyticsService,
              blockPlaceRepository: this.blockPlaceRepository
            }
          })
        }
      }),
      [SMI24_BLOCK_USE_CASE_KEY]: new Smi24BlockUseCase({
        deps: {
          analyticsService: new Smi24BlockAnalyticsService({
            pageName: PAGE_NAME,
            deps: {
              analytics: this.analyticsService,
              blockPlaceRepository: this.blockPlaceRepository
            }
          })
        }
      })
    };
  },
  inject: {
    analyticsService: {
      from: 'analyticsService'
    }
  },
  data() {
    return {
      isFeedbackToErrorModalOpen: false,
      isFeedbackToErrorModalRendered: false,
      isImageViewerVisible: false,
      isImageViewerRendered: false,
      isMatureContentModalOpen: true,
      destroy: new Subject()
    };
  },
  computed: {
    recordModule() {
      return useModule(this.$store, RECORD_MODULE_NAMESPACE);
    },
    blockPlaceRepository() {
      return new BlockPlaceRepository(AnalyticsBlockPlace.Central);
    },
    centralColumnBlocks() {
      return this.store.layoutModule.centralColumn;
    },
    recordData() {
      const recordBlock = this.store.layoutModule.record;
      return recordBlock && recordBlock.data ? recordBlock.data : {};
    },
    hasRecordData() {
      return Object.keys(this.recordData).length > 0;
    },
    recordId() {
      return this.recordData.id;
    },
    isDesktop() {
      return this.store.deviceInfo.isDesktop;
    },
    isHoldoutTest() {
      return this.store.layoutModule.isHoldoutTest;
    },
    isMature() {
      return this.recordData.isMatureContent;
    },
    isShowMatureOverlay() {
      return this.isMature && this.isMatureContentModalOpen;
    },
    isScroogeTest() {
      return this.store.layoutModule.isScroogeTest;
    }
  },
  beforeMount() {
    this.registerModule();
    this.isMatureContentModalOpen = this.isMature;
  },
  beforeDestroy() {
    this.destroy.next();
    this.destroy.unsubscribe();
  },
  destroyed() {
    unregisterModule(this.$store, RECORD_MODULE_NAMESPACE);
  },
  mounted() {
    this.showFeedBackError();
    this.imageViewerService();
  },
  methods: {
    registerModule() {
      registerModule(this.$store, RECORD_MODULE_NAMESPACE, new RecordModule(this.store));
    },
    bindComponent(block) {
      if (this.isHoldoutTest && block.type === 'flyroll_advert') {
        return null;
      }

      return blockToComponent(block, blockNameToComponentMap, false);
    },
    showFeedBackError() {
      document.addEventListener('keyup', event => {
        if (event.key === 'Enter' && event.ctrlKey) {
          this.isFeedbackToErrorModalOpen = true;
          this.isFeedbackToErrorModalRendered = true;
        }
      });
    },
    imageViewerService() {
      const imageViewerId = 'ivRecord';
      this.store.layoutModule.imageViewerService.state$
        .pipe(
          takeUntil(this.destroy),
          filter(state => state.id === imageViewerId)
        )
        .subscribe(state => {
          if (state.isOpen) {
            if (this.isImageViewerRendered) {
              this.store.layoutModule.imageViewerService.changeScrollState({
                value: 'disable'
              });
            }
            this.isImageViewerRendered = true;
            this.isImageViewerVisible = true;
          } else {
            this.isImageViewerVisible = false;
          }
        });
    },
    processingAdvView(type) {
      switch (type) {
        case 'smi2':
          this.sendAdvReachGoal('ViewSmi2Block', 'СМИ2 (блок)', 'Просмотр');
          break;
        case 'smi24':
          this.sendAdvReachGoal('WATCH_WIDGET_24SMI', '24СМИ (блок)', 'Просмотр');
          break;
        case 'sparrow':
          this.sendAdvReachGoal('ViewSparrowBlock', 'Sparrow (блок)', 'Просмотр');
          break;
        case 'relap':
          this.sendAdvReachGoal('ViewAlsoReadBlock', 'Читайте также (блок)', {
            Просмотр: this.recordId
          });
          break;
        default:
          break;
      }
    },
    processingAdvClick(type) {
      switch (type) {
        case 'smi2':
          this.sendAdvReachGoal('ClickSmi2Block', 'СМИ2 (блок)', 'Клик');
          break;
        case 'smi24':
          this.sendAdvReachGoal('CLICK_WIDGET_24SMI', '24СМИ (блок)', 'Клик');
          break;
        case 'sparrow':
          this.sendAdvReachGoal('ClickSparrowBlock', 'Sparrow (блок)', 'Клик');
          break;
        case 'relap':
          this.sendAdvReachGoal('ClickAlsoReadBlock', 'Читайте также (блок)', {
            Клик: this.recordId
          });
          break;
        default:
          break;
      }
    },
    sendAdvReachGoal(goalName, fieldType, valueName) {
      const blockType = 'Центральная колонка';
      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName
      });
    },
    onMatureContentModalClose() {
      this.isMatureContentModalOpen = false;
    }
  }
};
</script>

<style lang="scss" module>
@import 'styles';

.record-page {
  display: block;

  :global ._central {
    display: none;

    @include on-min-resize($tablet) {
      border-bottom: 8px solid $color-surface-200;
    }
    @include on-min-resize($laptop) {
      border-bottom: 1px solid $color-surface-200;
    }
    @include on-min-resize($desktop) {
      border-top: 1px solid $color-surface-200;
      border-bottom: none;
    }
  }
}

.record-page-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  z-index: 10;
}

.become_an_author {
  margin-bottom: $desk-bottom-indent;
}

.record_footer_author {
  margin-bottom: 16px;
}

.record_footer_author + .record_footer_tags > div {
  display: inline-block;

  width: 100%;
  margin-top: 16px;
}

.record_footer_tags {
  margin-bottom: $desk-bottom-indent;
}

.record_footer_votes {
  margin-bottom: $desk-bottom-indent;
}

.notify_about_typo {
  margin-bottom: $desk-bottom-indent;
}

.authors_records {
  margin-bottom: 16px;
}

.auto,
.forum {
  margin: 12px 0;

  @include on-min-resize($tablet) {
    margin: $desk-bottom-indent 0;
  }
}
</style>
